import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const LEFT_MENU = [
  { label: 'Værkstedet', href: '/vaerkstedet' },
  { label: 'Montageafdelingen', href: '/montageafd' },
  { label: 'Vandskæring', href: '/vandskaering' }
];

const RIGHT_MENU = [
  { label: 'Om os', href: '/om-os' },
  { label: 'Referencer', href: '/referencer' },
  { label: 'Seneste oplæg', href: '/nyheder' },
  { label: 'Kontakt', href: '/kontakt' }
];

const ALL_MENU = [...LEFT_MENU, ...RIGHT_MENU];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1200;
      setIsMobile(mobile);
      if (!mobile) {
        setIsOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // CSS Filter to convert white SVG/PNG logo to #0073A5
  const logoStyle = {};

  return (
    <header className={isHome 
      ? "absolute inset-x-0 top-10 z-50 bg-transparent" 
      : "relative z-50 bg-white border-b border-slate-100 shadow-xs"
    }>
      <div className="w-full px-6 lg:px-12 h-24 flex justify-between items-center">
        
        {/* Desktop Menu - Left Side */}
        {!isMobile && (
          <nav className="flex items-center gap-3 lg:gap-6 w-4/12 justify-start">
            {LEFT_MENU.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) => `
                  text-base font-semibold tracking-[0.12em] transition-all duration-200 py-2 border-b-2 whitespace-nowrap
                  ${isActive 
                    ? isHome ? 'text-white border-[#0073A5]' : 'text-[#0073A5] border-[#0073A5]'
                    : isHome ? 'text-white border-transparent' : 'text-slate-600 border-transparent hover:text-[#0073A5]'
                  }
                `}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}

        {/* Centered Logo */}
        {!isMobile && (
          <Link to="/" className="flex items-center justify-center shrink-0 mx-auto lg:mx-0 w-auto lg:w-4/12 px-8 lg:px-12">
            <img 
              src="https://assets.auxodev.dk/assets/814e446f-d222-4d23-8be7-5a6a25bb15ff/bf499a2e-a67d-4e48-b244-da86caa413fe.png" 
              alt="DAN-TEAM STAINLESS ApS" 
              style={logoStyle}
              className="h-16 lg:h-20 w-auto object-contain transition-transform duration-300 hover:scale-105"
            />
          </Link>
        )}

        {/* Desktop Menu - Right Side */}
        {!isMobile && (
          <nav className="flex items-center gap-3 lg:gap-6 w-4/12 justify-end">
            {RIGHT_MENU.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) => `
                  text-base font-semibold tracking-[0.12em] transition-all duration-200 py-2 border-b-2 whitespace-nowrap
                  ${isActive 
                    ? isHome ? 'text-white border-[#0073A5]' : 'text-[#0073A5] border-[#0073A5]'
                    : isHome ? 'text-white border-transparent' : 'text-slate-600 border-transparent hover:text-[#0073A5]'
                  }
                `}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}

        {/* Burger Button (Visible < 1200px) */}
        {isMobile && (
          <div className="flex justify-between items-center w-full">
            <Link to="/" className="flex items-center gap-3 group shrink-0 px-4">
              <img 
                src="https://assets.auxodev.dk/assets/814e446f-d222-4d23-8be7-5a6a25bb15ff/bf499a2e-a67d-4e48-b244-da86caa413fe.png" 
                alt="DAN-TEAM STAINLESS ApS" 
                style={logoStyle}
                className="h-12 w-auto object-contain"
              />
            </Link>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-md focus:outline-none transition-colors ${
                isHome ? 'text-white hover:text-[#0073A5]' : 'text-slate-800 hover:text-[#0073A5]'
              }`}
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        )}
      </div>

      {/* Mobile Menu (Visible < 1200px when open) */}
      {isMobile && isOpen && (
        <div className={`absolute top-24 left-0 w-full py-6 px-6 flex flex-col gap-2 shadow-xl animate-in slide-in-from-top duration-200 z-50 ${
          isHome 
            ? 'border-t border-slate-900 bg-slate-950/95 backdrop-blur-md' 
            : 'border-t border-slate-100 bg-white'
        }`}>
          {ALL_MENU.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              onClick={() => setIsOpen(false)}
              className={({ isActive }) => `
                px-4 py-3 text-base font-semibold tracking-widest rounded-md transition-all
                ${isActive 
                  ? isHome 
                    ? 'text-[#0073A5] bg-slate-900 border-l-4 border-[#0073A5] pl-3' 
                    : 'text-[#0073A5] bg-slate-50 border-l-4 border-[#0073A5] pl-3'
                  : isHome 
                    ? 'text-slate-300 hover:text-white hover:bg-slate-900/50' 
                    : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50'
                }
              `}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
